import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

interface UseIsInViewportResult<T extends Element> {
  readonly elementRef: RefObject<T | null>;
  readonly isInViewport: boolean;
}

// Loads once true and never resets: a thumbnail scrolled past shouldn't unload
// and re-fetch when it comes back into view.
export const useIsInViewport = <
  T extends Element,
>(): UseIsInViewportResult<T> => {
  const elementRef = useRef<T | null>(null);
  const [isInViewport, setIsInViewport] = useState(false);

  useEffect(() => {
    const element = elementRef.current;

    if (!element || isInViewport) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) {
          return;
        }
        setIsInViewport(true);
      },
      { rootMargin: '200px' },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [isInViewport]);

  return { elementRef, isInViewport };
};
